import RenderIf from 'src/components/RenderIf'
import Loading from 'src/components/Loading'
import { FormProvider, SubmitErrorHandler, SubmitHandler } from 'react-hook-form'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import Rop04JoinFormPageView from 'src/pages/Rop04JoinFormPage/view'
import { FormObjectType, JoinFormType } from 'src/types/react-hook-form'
import {
  CertNumberRequestSpace,
  CertNumberVerifySpace,
  JoinPossibleCheckByPcode,
  KTMVNOVerifySpace,
  VulnerableVerifySpace,
  TelecomNumberType,
  TermsListSpace,
  termsValueNumberType,
  UIInfoSpace,
} from 'src/types/api'
import { dynamicUiDefaultData } from 'src/service/constants'
import {
  joinCHeckByPcodeQueryOption,
  termsListQueryOption,
  uiInfoQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { useParamsList } from 'src/hooks/useParamsList.hook'
import { useModal } from 'src/components/Modal/provider'
import {
  certRequestQuery,
  certVerifyQuery,
  KTMVNOVerifyQuery,
  VulnerableVerifyQuery,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import useSetTimeout from 'src/hooks/useSetTimeout.hook'
import { useCountdown } from 'src/hooks/useCountDown.hook'
import {
  ERRCODE_SUCCESS,
  ERRCODES_OTPCERTREQUEST,
  ERRCODES_OTPCERTSUBMIT,
} from 'src/types/errcodes'
import {
  already_signup_dataLayer,
  d30_resignup_dataLayer,
  join_complete_dataLayer,
  otp_request_dataLayer,
} from 'src/service/utils/gtmDatalayer'
import { handleMoveToAppStore, handleMoveToSvcLoginPage } from 'src/service/utils'
import { parseUiData } from 'src/service/utils/decode'
import useRop04JoinForm from 'src/hooks/react-hook-form/useRop04JoinForm'
import { getTermsErrorMessage } from 'src/hooks/react-hook-form/shared/getTermsErrorMessage'
import RopTermsModalChildren from 'src/components/Modal/_component/RopTermsModalChildren'
import KTMVNOModalChildren from 'src/pages/Rop04JoinFormPage/components/Modal/KtMvnoModalChildren'
import { useToast } from 'src/hooks/useToast'
import useMoveScroll from 'src/hooks/useMoveScroll'
import VulnerableCheckModal from 'src/components/Modal/_component/VulnerableCheckModal'
import { join_complete_tnk } from 'src/service/utils/tnk'
import { captureMvnoTermsError } from 'src/service/utils/sentry/validation'

export interface Rop04JoinFormPageProps {
  dynamicUiData: Omit<UIInfoSpace.Response, 'UIData'> & { UIData: UIInfoSpace.UIDataType }
  registerAttribute: Record<keyof JoinFormType, Partial<FormObjectType>>
  allTermsAgreeHandler: (tf?: boolean) => void
  otpCertRequest: {
    submit: SubmitHandler<Pick<JoinFormType, 'PNumber'>>
    error: SubmitErrorHandler<JoinFormType>
  }
  otpCertVerify: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  }
  resetFormItems: () => void
  registerAttributeExcludeAllTerms: Partial<FormObjectType>[]
  count: string
  originCount: number
  blockTelList: string[] | null
  isOtpRequestPending: boolean
  isOtpVerifyPending: boolean
  isMvnoVerifyPending: boolean
  isVulnerableVerifyPending: boolean
  isPNumFilled: boolean
  isCertStep: boolean
  isCertFilled: boolean
  isOtpReady: boolean
  userPNumber: string
  didFlip: boolean
  isToastShow: boolean
  toastMsg: string
  bannerScrollRef: React.MutableRefObject<HTMLImageElement | null>
  agencyScrollRef: React.MutableRefObject<HTMLImageElement | null>
  pNumberScrollRef: React.MutableRefObject<HTMLImageElement | null>
  pNumberInputRef: React.MutableRefObject<HTMLInputElement | null>
  approvalNumberInputRef: React.MutableRefObject<HTMLInputElement | null>
  scrollToBanner: () => void
  scrollToAgency: () => void
  scrollToPNumber: () => void
}

const TNK_TRACKING_PCODES = ['tnk_01']
const STORE_REPLACE_FORMS = ['rop05']

const Rop04JoinFormPage = () => {
  const [blockTelList, setBlockTelList] = useState<string[] | null>(null)
  const { pCode, fixedTelecom, telecom, form, slotNo } = useParamsList()
  const { setCustomTimeout } = useSetTimeout()
  const { openModal, closeModal } = useModal()
  const { isShow: isToastShow, message: toastMsg, openToast } = useToast(2200)
  const [didFlip, setDidFlip] = useState(false)
  // 취약계층 확인(선행 API) 응답 전 가입 버튼 연타 시 가입 요청이 중복 전송되는 것을 막는다
  const isJoinSubmittingRef = useRef(false)

  const isTnkTracking = TNK_TRACKING_PCODES.includes(pCode as string)
  const isRedirectAppStore = STORE_REPLACE_FORMS.includes(form as string)

  const {
    data: { Body: { UIData, ...dynamicUi } = dynamicUiDefaultData } = {},
    isLoading: isDynamicUiLoading,
  } = useCustomQuery<UIInfoSpace.Response>(uiInfoQueryOption(pCode as string, slotNo))

  const { data: { Body: termsContents = undefined } = {}, isLoading: isTermsLoading } =
    useCustomQuery<TermsListSpace.Response>(termsListQueryOption())

  const { data: { Body: joinPossibleData = undefined } = {}, isSuccess: isJoinPossibleSuccess } =
    useCustomQuery<JoinPossibleCheckByPcode.Response>(joinCHeckByPcodeQueryOption(pCode))

  const { isPending: isOtpRequestPending, mutateAsync: otpRequestMutate } = useCustomMutation<
    CertNumberRequestSpace.Request,
    CertNumberRequestSpace.Response
  >(certRequestQuery())

  const { isPending: isOtpVerifyPending, mutateAsync: otpVerifyMutate } = useCustomMutation<
    CertNumberVerifySpace.Request,
    CertNumberVerifySpace.Response
  >(certVerifyQuery())

  const { isPending: isMvnoVerifyPending, mutateAsync: ktMvnoVerifyMutate } = useCustomMutation<
    KTMVNOVerifySpace.Request,
    KTMVNOVerifySpace.Response
  >(KTMVNOVerifyQuery())

  const { isPending: isVulnerableVerifyPending, mutateAsync: vulnerableVerifyMutate } =
    useCustomMutation<VulnerableVerifySpace.Request, VulnerableVerifySpace.Response>(
      VulnerableVerifyQuery(),
    )

  const { methods, registerAttribute, allTermsAgreeHandler, termsData } =
    useRop04JoinForm(termsContents)
  const { setFocus, setValue, watch, reset, getValues, resetField } = methods

  const ktMVNOAttribute = registerAttribute.KTMVNO

  // useMoveScroll 훅 추가
  const {
    RefElement: bannerScrollRef,
    RefInput: pNumberInputRef,
    onMoveToElement: scrollToBanner,
  } = useMoveScroll()

  const {
    RefElement: agencyScrollRef,
    RefInput: approvalNumberInputRef,
    onMoveToElement: scrollToAgency,
  } = useMoveScroll()

  const { RefElement: pNumberScrollRef, onMoveToElement: scrollToPNumber } = useMoveScroll()

  //인증번호 유효시간 초과
  const { countStart, count, countStop, originCount } = useCountdown({
    countExpiredCallbackFn: () => {
      setValue('TranID', '')
      setValue('ApprovalNumber', '')
      openModal({
        type: 'rop04',
        contents: { title: '유효시간이 초과되었습니다. 인증번호를 다시 받으시기 바랍니다.' },
        button: {
          submit: {
            text: '확인',
            func: () => {
              closeModal()
              scrollToBanner()
            },
          },
        },
      })
    },
  })

  const userPNumber = watch('PNumber')
  const approvalNumber = watch('ApprovalNumber')
  const tranId = watch('TranID')

  const isPNumFilled = userPNumber.length >= 9
  const isCertStep = tranId !== ''
  const isCertFilled = approvalNumber.length >= 4
  const isOtpReady = isCertFilled && originCount > 0

  const renderFullPNumber = (shortPNumber: string) => `010${shortPNumber.replace(' ', '')}`

  const blurAllInputs = () => {
    const active = document.activeElement as HTMLElement | null
    if (active && typeof active.blur === 'function') {
      active.blur()
    }
  }

  const handleCheckAllTerms = async () => {
    let isChecked = !!getValues('allTerms')
    if (!isChecked) {
      blurAllInputs()
      openModal({
        type: 'rop04',
        contents: {
          children: (
            <FormProvider {...methods}>
              <RopTermsModalChildren
                allTermsAgreeHandler={allTermsAgreeHandler}
                otpCertRequest={() => otpCertRequest.submit({ PNumber: getValues('PNumber') })}
                isLoading={isOtpRequestPending || isMvnoVerifyPending}
              />
            </FormProvider>
          ),
        },
      })
      return isChecked
    } else {
      return true
    }
  }

  const handleCheckKtMvnoUser = async () => {
    const Telecom = Number(fixedTelecom || telecom) as TelecomNumberType
    const ktAgency = Telecom === 1
    const fullPNumber = renderFullPNumber(getValues('PNumber'))
    if (!ktAgency || getValues('KTMVNO')) return true

    return await ktMvnoVerifyMutate({ PNumber: fullPNumber, Telecom }).then((res) => {
      captureMvnoTermsError(res.Body)

      const isMVNO = res.Body.Type === 1
      if (!isMVNO) return true
      else {
        blurAllInputs()
        openModal({
          type: 'rop04',
          contents: {
            children: (
              <FormProvider {...methods}>
                <KTMVNOModalChildren
                  {...res.Body}
                  ktMVNOAttribute={ktMVNOAttribute}
                  otpCertRequest={() => otpCertRequest.submit({ PNumber: getValues('PNumber') })}
                  isLoading={isOtpRequestPending}
                />
              </FormProvider>
            ),
          },
        })
        return false
      }
    })
  }

  const handleCheckVulnerableUser = async () => {
    const Telecom = Number(fixedTelecom || telecom) as TelecomNumberType
    const fullPNumber = renderFullPNumber(getValues('PNumber'))
    return await vulnerableVerifyMutate({ PNumber: fullPNumber, Telecom }).then((res) => {
      const { Vulnerable, AgePopUpYN } = res.Body
      const isVulnerable = Vulnerable === 'Y' || AgePopUpYN === 'Y'
      if (!isVulnerable) return true

      return new Promise<boolean>((resolve) => {
        openModal({
          contents: {
            children: (
              <VulnerableCheckModal
                onConfirm={() => {
                  resolve(true)
                }}
                onCancel={() => {
                  resolve(false)
                }}
                telecom={Telecom}
              />
            ),
          },
        })
      })
    })
  }

  const otpCertRequest: {
    submit: SubmitHandler<Pick<JoinFormType, 'PNumber'>>
    error: SubmitErrorHandler<JoinFormType>
  } = {
    submit: (data) => {
      const Telecom = Number(fixedTelecom || telecom) as TelecomNumberType
      const { PNumber } = data
      const fullPNumber = renderFullPNumber(PNumber)

      handleCheckAllTerms().then((isChecked) => {
        if (isChecked) {
          handleCheckKtMvnoUser().then((isNotKtMvno) => {
            if (isNotKtMvno) {
              handleOtpRequest()
            }
          })
        }
      })

      const handleOtpRequest = () => {
        otpRequestMutate({ PNumber: fullPNumber, Telecom, PCode: pCode, TimeOut: 180 })
          .then((res) => {
            const { Body, Header } = res
            switch (Header.ErrCode) {
              case ERRCODE_SUCCESS:
                const { TranID, TimeOut } = Body
                setValue('TranID', TranID)
                if (originCount > 0) openToast('인증번호가 재발송되었습니다.')
                countStart(TimeOut)
                otp_request_dataLayer()
                scrollToAgency()
                break
              case ERRCODES_OTPCERTREQUEST.Error5timesLimit:
              default:
                resetFormItems()
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: { submit: { text: '확인' } },
                })
                break
              case ERRCODES_OTPCERTREQUEST.ErrorAlreadyMember:
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: {
                    submit: {
                      text: '확인',
                      func: () => {
                        already_signup_dataLayer()
                        handleMoveToAppStore()
                      },
                    },
                  },
                })
                break
              case ERRCODES_OTPCERTREQUEST.Error30DaysLimit:
                d30_resignup_dataLayer()
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: { submit: { text: '확인' } },
                })
                break
              case ERRCODES_OTPCERTREQUEST.ErrorSpMember:
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: {
                    submit: {
                      text: '확인',
                      func: () => {
                        resetFormItems()
                        closeModal()
                      },
                    },
                  },
                })
                break
              case ERRCODES_OTPCERTREQUEST.ErrorPNumber:
              case ERRCODES_OTPCERTREQUEST.ErrorNoTelecom:
              case ERRCODES_OTPCERTREQUEST.ErrorAuthMsgTime:
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: { submit: { text: '확인' } },
                })
                break
            }
          })
          .catch((err: Error) => {
            console.error(err)
          })
      }
    },
    error: (errors, event) => {
      const { PNumber } = errors
      const errorMsg = PNumber?.message
      const termsErrorMsg = getTermsErrorMessage(errors)

      if (!!errorMsg) {
        openModal({
          type: 'rop04',
          contents: { title: errorMsg },
          button: { submit: { text: '확인' } },
        })
        window.focus()
        window.onfocus = () => {
          setCustomTimeout(() => setFocus('PNumber', { shouldSelect: true }), 500)
        }
      } else if (!!termsErrorMsg) {
        openModal({
          type: 'rop04',
          contents: {
            children: (
              <FormProvider {...methods}>
                <RopTermsModalChildren
                  allTermsAgreeHandler={allTermsAgreeHandler}
                  otpCertRequest={() => otpCertRequest.submit({ PNumber: getValues('PNumber') })}
                  isLoading={isOtpRequestPending || isMvnoVerifyPending}
                />
              </FormProvider>
            ),
          },
        })
      }
      console.warn(errors)
    },
  }

  const otpCertVerify: {
    submit: SubmitHandler<JoinFormType>
    error: SubmitErrorHandler<JoinFormType>
  } = {
    submit: (data) => {
      const {
        PNumber,
        TranID,
        ApprovalNumber,
        Service,
        SKTPrivacy,
        KTPrivacy,
        LGUPPrivacy,
        Insurance,
        PrivacyConsent,
        PrivacyThirdParty,
        Marketing,
        KTMVNO,
      } = data
      const fullPNumber = renderFullPNumber(PNumber)
      const otpVerifySubmitData: CertNumberVerifySpace.Request = {
        Telecom: Number(telecom) as TelecomNumberType,
        PNumber: fullPNumber,
        TranID,
        ApprovalNumber,
        TermsIdx: termsData?.Idx,
        AgreeDetail: {
          ServiceAgree: Number(Service) as termsValueNumberType,
          PrivacyConsentAgree: Number(PrivacyConsent) as termsValueNumberType,
          PrivacyThirdPartyAgree: Number(PrivacyThirdParty) as termsValueNumberType,
          SKTPrivacyAgree: Number(SKTPrivacy) as termsValueNumberType,
          KTPrivacyAgree: Number(KTPrivacy) as termsValueNumberType,
          LGUPPrivacyAgree: Number(LGUPPrivacy) as termsValueNumberType,
          InsuranceAgree: Number(Insurance) as termsValueNumberType,
          MarketingAgree: Number(Marketing) as termsValueNumberType,
          KTMVNOAgree: Number(KTMVNO) as termsValueNumberType,
        },
        PCode: pCode,
        //reward api authkey (reward 존재 시)
        AuthKey: '',
      }
      if (getValues('TranID') === '') {
        openModal({
          type: 'rop04',
          contents: { title: '인증번호 받기를 먼저 시도하여 주세요.' },
          button: {
            submit: {
              text: '확인',
              func: () => {
                closeModal()
                scrollToBanner()
              },
            },
          },
        })
        return
      }
      if (getValues('ApprovalNumber')?.length < 4) {
        openModal({
          type: 'rop04',
          contents: { title: '인증번호를 입력해주세요.' },
          button: {
            submit: {
              text: '확인',
              func: () => {
                closeModal()
                scrollToAgency()
              },
            },
          },
        })
        return
      }

      if (isJoinSubmittingRef.current) return
      isJoinSubmittingRef.current = true

      handleCheckVulnerableUser()
        .then((isVulnerable) => {
          if (isVulnerable) {
            return handleOtpVerify()
          }
        })
        .finally(() => {
          isJoinSubmittingRef.current = false
        })

      const handleOtpVerify = () => {
        return otpVerifyMutate(otpVerifySubmitData)
          .then((res) => {
            const { Header } = res
            switch (Header.ErrCode) {
              case ERRCODE_SUCCESS:
                join_complete_dataLayer(fullPNumber)
                if (isTnkTracking) join_complete_tnk()

                if (isRedirectAppStore) {
                  openModal({
                    type: 'rop04',
                    contents: {
                      title: '휴대폰쿠폰지갑 안내',
                      desc: '휴대폰에서 모바일쿠폰을 상시 할인 및 특가에 구매할 수 있는 유료부가서비스에 가입되셨습니다. 원활한 이용을 위해 [확인] 버튼을 눌러 앱스토어로 이동하세요.',
                    },
                    button: {
                      submit: {
                        text: '확인',
                        func: () => handleMoveToAppStore(),
                      },
                    },
                  })
                } else {
                  openModal({
                    type: 'rop04',
                    contents: {
                      title: '휴대폰쿠폰지갑 안내',
                      desc: '휴대폰에서 모바일쿠폰을 상시 할인 및 특가에 구매할 수 있는 유료부가서비스에 가입되셨습니다.\n원활한 이용을 위해 [확인] 버튼을 눌러 서비스페이지로 이동하세요.',
                    },
                    button: {
                      submit: {
                        text: '확인',
                        func: () =>
                          handleMoveToSvcLoginPage(
                            pCode,
                            Number(telecom) as TelecomNumberType,
                            fullPNumber,
                          ),
                      },
                    },
                  })
                }

                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNoRequest:
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: { submit: { text: '확인' } },
                })
                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumberWrong:
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: {
                    submit: {
                      text: '확인',
                      func: () => {
                        resetField('ApprovalNumber')
                        scrollToAgency()
                        closeModal()
                      },
                    },
                  },
                })
                break
              case ERRCODES_OTPCERTSUBMIT.ErrorAuthMsgNumber3Wrong:
                openModal({
                  type: 'rop04',
                  contents: { title: Header.ErrMsg },
                  button: {
                    submit: {
                      text: '확인',
                      func: () => {
                        window.location.reload()
                      },
                    },
                  },
                })
                break
            }
          })
          .catch((err: Error) => {
            console.error(err)
          })
      }
    },
    error: (errors, event) => {
      const { PNumber, TranID } = errors
      const errorMsg = TranID?.message || PNumber?.message
      const termsErrorMsg = getTermsErrorMessage(errors)

      switch (true) {
        case !!errorMsg:
          openModal({
            type: 'rop04',
            contents: { title: errorMsg },
            button: { submit: { text: '확인' } },
          })
          break
        case !!termsErrorMsg:
          blurAllInputs()
          openModal({
            type: 'rop04',
            contents: {
              children: (
                <FormProvider {...methods}>
                  <RopTermsModalChildren
                    allTermsAgreeHandler={allTermsAgreeHandler}
                    otpCertRequest={() => otpCertVerify.submit({ ...getValues() })}
                    isLoading={
                      isOtpRequestPending || isMvnoVerifyPending || isVulnerableVerifyPending
                    }
                  />
                </FormProvider>
              ),
            },
          })
          break
        default:
          return
      }
      console.warn(errors)
    },
  }

  const registerAttributeExcludeAllTerms: () => Partial<FormObjectType>[] = useCallback(() => {
    const objectEntriesAttribute = Object.entries(registerAttribute)

    return objectEntriesAttribute
      .filter((attributeArray) => attributeArray[0] !== 'allTerms')
      .map((attribute) => attribute[1])
      .filter((element) => element.termsTitle !== undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerAttribute, telecom])

  const resetFormItems = () => {
    countStop()
    reset()
  }

  // 초기화 및 초기 세팅
  useEffect(() => {
    resetFormItems()
    pNumberInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 가입폼 하단 애니메이션 제어
  useEffect(() => {
    if (!didFlip && isCertFilled) setDidFlip(true)
  }, [isCertFilled, didFlip])

  // Pcode 별 통신사 가입 제한 팝업
  useEffect(() => {
    if (isJoinPossibleSuccess) {
      const telecomToJoinPossibleData = {
        '0': joinPossibleData?.SKTJoin,
        '1': joinPossibleData?.KTJoin,
        '2': joinPossibleData?.LGUPJoin,
      }

      // 가입 제한 통신사 목록 설정
      setBlockTelList(
        Object.entries(telecomToJoinPossibleData)
          .filter(([_key, value]) => value === 'N')
          .map(([key]) => key),
      )
      const isBlockTelecom = telecomToJoinPossibleData[fixedTelecom || telecom] === 'N'

      if (isBlockTelecom) {
        const dimLayer = document.createElement('div')
        Object.assign(dimLayer.style, {
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: '1000',
          pointerEvents: 'none',
        })

        document.body.appendChild(dimLayer)
        setTimeout(() => {
          openModal({
            type: 'rop04',
            contents: { title: joinPossibleData?.JoinBlockMsg },
            button: {
              submit: {
                text: '확인',
                func: () => {
                  window.self.close()
                  window.history.back()
                },
              },
            },
          })
        }, 200)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJoinPossibleSuccess, telecom])

  const props: Rop04JoinFormPageProps = {
    count,
    originCount,
    otpCertVerify,
    otpCertRequest,
    registerAttribute,
    allTermsAgreeHandler,
    resetFormItems,
    registerAttributeExcludeAllTerms: registerAttributeExcludeAllTerms(),
    dynamicUiData:
      dynamicUiDefaultData.UIData !== UIData && UIData !== ''
        ? { UIData: parseUiData(UIData), ...dynamicUi }
        : dynamicUiDefaultData,
    blockTelList,
    isOtpRequestPending,
    isOtpVerifyPending,
    isMvnoVerifyPending,
    isVulnerableVerifyPending,
    isPNumFilled,
    isCertStep,
    isCertFilled,
    isOtpReady,
    userPNumber,
    didFlip,
    isToastShow,
    toastMsg,
    bannerScrollRef,
    agencyScrollRef,
    pNumberScrollRef,
    pNumberInputRef,
    approvalNumberInputRef,
    scrollToBanner,
    scrollToAgency,
    scrollToPNumber,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf when={isDynamicUiLoading || isTermsLoading}>
        <Loading />
      </RenderIf>
      <Rop04JoinFormPageView {...props} />
    </FormProvider>
  )
}

export default Rop04JoinFormPage
